// @flow

import type { Node } from 'react';
import styled from 'styled-components';
import { Popover, Button, Menu, MenuItem, Position } from '@blueprintjs/core';
import * as RoleModel from '../../models/role';
import * as UserListModel from '../../models/user-list';

const RoleDescription = styled.div`
  opacity: 0.75;
`;
const RoleLabelLines = styled.div`
  text-align: left;
`;
const RequiredLabel = styled.span`
  font-size: 0.9em;
  margin-left: 0.7em;
`;
// override the disabled styles for the Blueprint menu
const StyledMenu = styled(Menu)`
  .bp3-menu-item.bp3-disabled {
    color: #182026 !important;
  }

  .bp3-menu-item.bp3-disabled > .bp3-icon,
  .bp3-menu-item > .bp3-icon {
    color: #0d8050 !important;
  }
`;

type simpleRoleT = { id: number };

type Props = {
  rolesCatalogue: RoleModel.RolesCatalogueT,
  // Read-only so that a caller's `roleDataT[]` is accepted: Flow's arrays are
  // invariant, but $ReadOnlyArray is covariant in its element type.
  selectedRoles?: $ReadOnlyArray<UserListModel.roleDataT | simpleRoleT>,
  onToggle(role: RoleModel.catalogueRoleT): any,
  disabled?: boolean,
  updating?: boolean,
  rolesDataAvailable?: boolean
};

type ReadOnlyProps = {
  text: Node,
  loading?: boolean
};

// The button shows the held roles one label per line, or the prompt when
// none are held.
const heldRolesText = (entries: Array<{ id: number, label: string }>): Node =>
  entries.length === 0 ? (
    'Select a role'
  ) : (
    <RoleLabelLines>
      {entries.map(entry => (
        <div key={entry.id}>{entry.label}</div>
      ))}
    </RoleLabelLines>
  );

// The picker with no menu behind it, for the states where there is nothing
// truthful to offer. The Popover has to be disabled alongside the Button,
// otherwise the (absent) menu is still openable.
const ReadOnlyPicker = ({ text, loading }: ReadOnlyProps) => (
  <Popover disabled position={Position.LEFT}>
    <Button loading={loading} text={text} disabled icon="caret-down" />
  </Popover>
);

const RolePicker = ({
  rolesCatalogue,
  selectedRoles = [],
  disabled,
  onToggle,
  updating,
  rolesDataAvailable = true
}: Props) => {
  // This row's user list loaded without its roles, so nothing can be ticked
  // and there are no held roles to name.
  if (!rolesDataAvailable) {
    return <ReadOnlyPicker text="Roles unavailable" />;
  }

  if (rolesCatalogue.status === 'loading') {
    return (
      <ReadOnlyPicker
        text="Select a role"
        loading={updating || rolesCatalogue.status === 'loading'}
      />
    );
  }

  if (rolesCatalogue.status === 'error') {
    // Without the catalogue the options can't be listed, but the roles the
    // user holds still come through on the user's embedded role rows.
    const heldRoles = selectedRoles
      .filter(selectedRole => (selectedRole: Object).clientAvailable !== false)
      .map(selectedRole => ({
        id: selectedRole.id,
        label: RoleModel.roleLabel((selectedRole: Object))
      }))
      .filter(entry => entry.label !== '');

    return <ReadOnlyPicker text={heldRolesText(heldRoles)} />;
  }

  const rolesForDisplay = rolesCatalogue.roles.map(role => ({
    role,
    selected: selectedRoles.some(selectedRole => selectedRole.id === role.id)
  }));

  const roleMenuLabel = heldRolesText(
    rolesForDisplay
      .filter(r => r.selected)
      .map(r => ({ id: r.role.id, label: RoleModel.roleLabel(r.role) }))
  );

  return (
    <Popover disabled={disabled} position={Position.LEFT}>
      <Button
        loading={updating}
        text={roleMenuLabel}
        disabled={disabled}
        icon="caret-down"
      />
      <StyledMenu>
        {rolesForDisplay.map(({ role, selected }) => (
          <MenuItem
            key={role.id}
            text={
              <div>
                <div>
                  {RoleModel.roleLabel(role)}
                  {role.defaultRole && <RequiredLabel>(required)</RequiredLabel>}
                </div>
                <RoleDescription>
                  {RoleModel.roleDescription(role)}
                </RoleDescription>
              </div>
            }
            icon={selected ? 'tick' : 'blank'}
            onClick={() => onToggle(role)}
            // Required roles can't be given up, but one the user is missing
            // stays clickable so an admin can grant it.
            disabled={(role.defaultRole && selected) || updating}
            shouldDismissPopover={false}
          />
        ))}
      </StyledMenu>
    </Popover>
  );
};

export default RolePicker;
